import type {
  Coordinate,
  AccommodationProductType,
  AccommodationRule,
  ActivityProductType,
  AmenitiesFilter,
  BoardBasisFilter,
  ExperienceFilter,
  HeartedHotel,
  NeighborhoodPolygonsOnlyHotel,
  SearchTerm,
  StarsRatingFilter,
} from '@libs/trip-planner-interfaces';
import {
  AccommodationType,
  AvailabilityOrder,
  IngredientType,
} from '@libs/trip-planner-interfaces';

import type { ActivityIngredient } from './activityIngredient';
import { DestiationIngredient } from './destinationIngredient';

export interface HotelIngredientFilters {
  carefullySelectedOnly: boolean;
  selectedHotelTypes: AccommodationProductType[];
  selectedActivityTypes: ActivityProductType[];
  selectedAmenitiesFilters: AmenitiesFilter[];
  selectedExperienceFilters: ExperienceFilter[];
  selectedHotelRules: AccommodationRule[];
  boardBasis: BoardBasisFilter[];
  polygonIds: string[];
  filterText: string;
  activitiesFilterText: string;
  starRating: StarsRatingFilter;
}

export const DEFAULT_AVAILABILITY_ORDER_SUGGESTION = AvailabilityOrder.Popular;

// TODO:
// Some methods in this class are used only here and could be changed to private, but with current state of the application this breaks type system for no apparent reason. -> https://trello.com/c/K8abwjqW
export class HotelIngredient extends DestiationIngredient<IngredientType.Hotel> {
  public static get defaultFilters(): HotelIngredientFilters {
    return {
      starRating: 0,
      carefullySelectedOnly: false,
      selectedHotelTypes: [],
      selectedActivityTypes: [],
      selectedAmenitiesFilters: [],
      selectedExperienceFilters: [],
      selectedHotelRules: [],
      boardBasis: [],
      polygonIds: [],
      filterText: '',
      activitiesFilterText: '',
    };
  }

  public static numberFilters = ['starRating'] as const;
  public static booleanFilters = ['carefullySelectedOnly'] as const;
  public static textFilters = ['filterText', 'activitiesFilterText'] as const;
  public static arrayFilters = [
    'selectedHotelTypes',
    'selectedActivityTypes',
    'selectedAmenitiesFilters',
    'selectedExperienceFilters',
    'selectedHotelRules',
    'boardBasis',
    'polygonIds',
  ] as const;

  public heartedHotels: HeartedHotel[] = [];
  public subIngredients: ActivityIngredient[] = [];
  public availabilityOrder: AvailabilityOrder =
    DEFAULT_AVAILABILITY_ORDER_SUGGESTION;

  public commonPolygon?: {
    name?: string;
    id?: number;
  };
  public nearbyPolygon?: {
    name?: string;
    id?: number;
  };

  public neighborhoodPolygons: NeighborhoodPolygonsOnlyHotel[] = [];

  public isDestinationPlaceholder = false;
  public positionFromSearchText?: Coordinate;

  public filters: HotelIngredientFilters = HotelIngredient.defaultFilters;

  // NOTE: HotelIngredient is sometimes used as a placeholder for ending location,
  // good moment to fix that will be during hotel and destination separation
  constructor(searchTerm: SearchTerm, isDestinationPlaceholder?: boolean) {
    super(IngredientType.Hotel, searchTerm);

    if (isDestinationPlaceholder) {
      this.isDestinationPlaceholder = isDestinationPlaceholder;
    }
  }

  public get heartedHotelCodes(): string[] {
    return this.heartedHotels.map((hotel: HeartedHotel) => hotel.code);
  }

  public get subIngredientCodes(): string[] {
    return this.subIngredients.map(
      (subIngredient: ActivityIngredient) => subIngredient.code
    );
  }

  public isIngredientCorrect(): boolean {
    return true;
  }

  public getSelectedAccommodationTypes(): AccommodationProductType[] {
    let selectedAccommodationTypes = this.filters.selectedHotelTypes;

    if (selectedAccommodationTypes.length === 0) {
      selectedAccommodationTypes = [
        AccommodationType.Apartment,
        AccommodationType.Hotel,
        AccommodationType.HouseOrCottage,
      ];
    }

    return selectedAccommodationTypes;
  }

  public getSelectedActivityTypes(): ActivityProductType[] {
    let selectedActivityTypes = this.filters.selectedActivityTypes;

    if (selectedActivityTypes.length === 0) {
      selectedActivityTypes = [
        AccommodationType.Golf,
        AccommodationType.Ticket,
      ];
    }

    return selectedActivityTypes;
  }

  public resetAllFilters(): void {
    this.filters = HotelIngredient.defaultFilters;
  }

  public hasActiveFilters(): boolean {
    return this.getActiveFiltersCount() > 0;
  }

  public getActiveFiltersCount(): number {
    let numberOfActiveFilters = 0;

    HotelIngredient.numberFilters.forEach((filter) => {
      if (this.filters[filter]) {
        numberOfActiveFilters++;
      }
    });

    HotelIngredient.booleanFilters.forEach((filter) => {
      if (this.filters[filter]) {
        numberOfActiveFilters++;
      }
    });

    HotelIngredient.textFilters.forEach((filter) => {
      if (this.filters[filter]) {
        numberOfActiveFilters++;
      }
    });

    HotelIngredient.arrayFilters.forEach((filter) => {
      if (this.filters[filter].length > 0) {
        numberOfActiveFilters =
          numberOfActiveFilters + this.filters[filter].length;
      }
    });

    return numberOfActiveFilters;
  }

  public getActiveFilterNames(): string[] {
    const activeFilterNames: string[] = [];

    HotelIngredient.numberFilters.forEach((filter) => {
      if (this.filters[filter]) {
        activeFilterNames.push(filter);
      }
    });

    HotelIngredient.booleanFilters.forEach((filter) => {
      if (this.filters[filter]) {
        activeFilterNames.push(filter);
      }
    });

    HotelIngredient.textFilters.forEach((filter) => {
      if (this.filters[filter]) {
        activeFilterNames.push(filter);
      }
    });

    HotelIngredient.arrayFilters.forEach((filter) => {
      if (this.filters[filter].length > 0) {
        activeFilterNames.push(filter);
      }
    });

    return activeFilterNames;
  }
}
